import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import type { RecipeListItem, RecipePublic } from "../lib/types";

function formatRelative(iso: string): string {
  const then = new Date(iso);
  const now = new Date();
  const days = Math.floor((now.getTime() - then.getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 28) return `${Math.floor(days / 7)} wk ago`;
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
  }).format(then);
}

function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function RecipesIndexPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery<RecipeListItem[]>({
    queryKey: ["recipes"],
    queryFn: () => api.get<RecipeListItem[]>("/api/recipes"),
  });

  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importError, setImportError] = useState<string | null>(null);

  const importMutation = useMutation({
    mutationFn: (url: string) =>
      api.post<RecipePublic>("/api/recipes/import", { url }),
    onSuccess: (recipe) => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      setShowImport(false);
      setImportUrl("");
      nav(`/recipes/${recipe.id}`);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setImportError(e.detail);
      else setImportError("Could not import recipe.");
    },
  });

  const onImportSubmit = (e: FormEvent) => {
    e.preventDefault();
    setImportError(null);
    if (!importUrl.trim()) return;
    importMutation.mutate(importUrl.trim());
  };

  return (
    <Shell>
      <section className="mb-20 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 01</div>
          <div className="label mt-1 text-ink-walnut">
            {data?.length ?? "—"} {data?.length === 1 ? "entry" : "entries"}
          </div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h2 className="display-1">
            The <span className="display-italic">Recipes</span>
          </h2>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
            <p className="max-w-xl font-body text-xl italic leading-snug text-ink-walnut">
              A small library of what we cook — pressed flat between the
              pages, gathered slowly, kept for keeping.
            </p>
            <div className="flex shrink-0 flex-wrap items-center gap-x-8 gap-y-3">
              <button
                type="button"
                onClick={() => {
                  setShowImport((s) => !s);
                  setImportError(null);
                }}
                className="label ink-link text-ink"
              >
                Import from URL
              </button>
              <Link to="/recipes/new" className="btn-ink">
                + New entry
              </Link>
            </div>
          </div>

          {showImport && (
            <form
              onSubmit={onImportSubmit}
              className="mt-10 border-l-2 border-sienna/60 pl-6 animate-fade-up"
            >
              <div className="label mb-3 text-sienna">N° — Import from URL</div>
              <p className="mb-5 max-w-lg font-body text-sm italic text-ink-walnut">
                Paste a recipe URL — BBC Good Food, NYT Cooking, Serious Eats,
                most major sites. The matcher reads the page and adds the
                recipe to the library.
              </p>
              <div className="flex flex-wrap items-baseline gap-6">
                <input
                  type="url"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://www.bbcgoodfood.com/recipes/…"
                  required
                  disabled={importMutation.isPending}
                  className="field-input min-w-[280px] flex-1"
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={importMutation.isPending || !importUrl.trim()}
                  className="btn-ink"
                >
                  {importMutation.isPending ? "Reading the page…" : "Import"}
                </button>
              </div>
              {importMutation.isPending && (
                <p className="mt-4 font-body text-xs italic text-ink-muted">
                  Trying JSON-LD first; falling back to Claude if the page
                  doesn't have it. A few seconds for familiar sites, longer
                  for unfamiliar ones.
                </p>
              )}
              {importError && (
                <div className="mt-4 font-body text-sm italic text-sienna">
                  {importError}
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {isLoading && (
        <div className="label animate-fade-in text-ink-muted">
          Gathering the library…
        </div>
      )}

      {isError && (
        <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna">
          The library could not be opened. Try again in a moment.
        </div>
      )}

      {data && data.length === 0 && (
        <div
          className="py-24 text-center animate-fade-up"
          style={{ animationDelay: "200ms" }}
        >
          <Ornament
            className="mx-auto mb-10 h-5 w-28 text-sienna"
            variant="default"
          />
          <h3 className="display-2">
            The pages are <span className="display-italic">empty.</span>
          </h3>
          <p className="mx-auto mt-6 max-w-md font-body text-lg italic text-ink-walnut">
            Write the first recipe to begin the book.
          </p>
          <Link to="/recipes/new" className="btn-ink mt-12 inline-flex">
            Write the first recipe
          </Link>
        </div>
      )}

      {data && data.length > 0 && (
        <ol className="divide-y divide-ink/15">
          {data.map((r, i) => (
            <li
              key={r.id}
              className="animate-fade-up"
              style={{ animationDelay: `${220 + i * 50}ms` }}
            >
              <Link
                to={`/recipes/${r.id}`}
                className="group grid grid-cols-12 items-baseline gap-x-6 gap-y-2 py-9 transition-colors duration-300 hover:bg-parchment-deep/50"
              >
                <div className="label col-span-3 text-ink-walnut md:col-span-1">
                  N° {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-9 md:col-span-7">
                  <h3 className="font-display text-[40px] font-light leading-[0.98] tracking-[-0.01em] text-ink transition-colors duration-300 group-hover:text-sienna md:text-5xl">
                    {r.title}
                  </h3>
                  {r.source_url && (
                    <div className="label mt-3 text-ink-muted">
                      sourced &nbsp;·&nbsp; {hostnameOf(r.source_url)}
                    </div>
                  )}
                </div>
                <div className="label col-span-6 text-ink-walnut md:col-span-2 md:text-right">
                  {r.servings} {r.servings === 1 ? "serving" : "servings"}
                </div>
                <div className="label col-span-6 text-ink-walnut md:col-span-2 md:text-right">
                  {formatRelative(r.updated_at)}
                </div>
              </Link>
            </li>
          ))}
        </ol>
      )}
    </Shell>
  );
}
